/**
 * Shared derivation of a hexagon "cell stats" object from a per-class
 * `detections` map. Used both per-cell (collectionHexagonsToCells) and for the
 * aggregate summary (collectionHexagonsSummary), so the two can never drift.
 *
 * The collection endpoint only returns per-class stats; the model-based flow, by
 * contrast, exposes a cell-level aggregate that the pure matrix classifiers read
 * via `getPropertyValue(meta, id)`. `deriveCellStats` bridges the two: it turns a
 * `detections` map into that flat, classifier-ready aggregate.
 *
 * @module hexagonCellStats
 */

import { enrichStemEstimates } from '@utils/map/stemEnrichment';

/**
 * Count-weighted mean of a per-class numeric field. Classes with a null value or
 * a non-positive count carry no signal and are skipped. Returns `null` when no
 * class contributes — so "no data" stays distinguishable from a genuine 0.
 *
 * @param {Array<object>} classes
 * @param {string} field
 * @returns {number|null}
 */
export const countWeightedMean = (classes, field) => {
	let weighted = 0;
	let weight = 0;
	for (const cls of classes) {
		const value = cls?.[field];
		const count = cls?.count ?? 0;
		if (value == null || count <= 0) continue;
		weighted += value * count;
		weight += count;
	}
	return weight > 0 ? weighted / weight : null;
};

/**
 * Sum a value picked from each class, treating null/undefined as 0.
 *
 * @param {Array<object>} classes
 * @param {(cls: object) => number|null|undefined} pick
 * @returns {number}
 */
export const sumBy = (classes, pick) =>
	classes.reduce((sum, cls) => sum + (cls ? (pick(cls) ?? 0) : 0), 0);

/**
 * Derive the flat, classifier-ready stats for a set of per-class detections.
 *
 * Derivations:
 *   - `count`            = Σ class.count
 *   - `mean_height`      = count-weighted mean of class.mean_height
 *   - `sum_object_area`  = Σ class.sum_object_area
 *   - `mean_object_area` = sum_object_area / count (exact mean; null if empty)
 *   - `total_area`       = the same Σ class.sum_object_area, exposed under the id
 *                          the shared `getPropertyValue` resolves FIRST — it reads
 *                          `meta.total_area ?? meta.area`, so publishing the key
 *                          here is what makes the object-area property report
 *                          detected area instead of falling through to the
 *                          container area. Because this function is the single
 *                          bridge both collection flows go through, exposing it
 *                          once fixes hexagon cells AND DSP plots without editing
 *                          the shared classification module. Model cells never set
 *                          the key, so their `?? meta.area` fallback is untouched.
 *   - `area`             = the ground area in m² (a cell's resolution, or the
 *                          total covered area for a summary) — needed so density
 *                          and per-ha stems can be computed. Stays the CONTAINER
 *                          area: it is a parallel value to `total_area`, not a
 *                          replacement for it.
 *   - stem fields        = via the shared `enrichStemEstimates` (totals + per-ha +
 *                          per-class); absent when no class carries stem data
 *
 * @param {object} detections - per-class stats map
 * @param {number} area - ground area in m²
 * @returns {object} flat cell-level stats
 */
export const deriveCellStats = (detections, area) => {
	const classes = Object.values(detections ?? {});
	const count = sumBy(classes, cls => cls.count);
	const sumObjectArea = sumBy(classes, cls => cls.sum_object_area);

	return {
		count,
		mean_height: countWeightedMean(classes, 'mean_height'),
		sum_object_area: sumObjectArea,
		mean_object_area: count > 0 ? sumObjectArea / count : null,
		// Same figure as `sum_object_area`, under the id the shared
		// `getPropertyValue` resolves ahead of `area`. `sumBy` returns 0 rather
		// than nullish, so a cell with no detections reports 0 — never null, and
		// never the container area.
		total_area: sumObjectArea,
		area,
		// `area` is what lets the enrichment compute per-ha stem densities.
		...enrichStemEstimates(detections, area),
	};
};
